import React, {useRef, useState} from 'react'
import Select from 'react-select'
import ApplicationResponsiveModal from '../../common/ApplicationResponsiveModal'
import {ScaffoldSubmission} from '../renterHomeUtils'
import {
  FoundPlaceConfirmationRequest,
  FoundPlaceOutcomeType,
  isLiveOutcomeSubmission,
} from '../../../models/found-place'
import MessageHelperModal from './MessageHelperModal'

type FoundPlaceModalProps = {
  isOpen: boolean
  submissions: ScaffoldSubmission[]
  initialView?: FoundPlaceModalView
  onClose: () => void
  onConfirm: (request: FoundPlaceConfirmationRequest) => Promise<void>
  onDelete: () => Promise<void>
  onRevokeAll: () => Promise<void>
}

export type FoundPlaceModalView =
  | 'options'
  | 'delete-options'
  | 'details'
  | 'card-options'
  | 'helper'
  | 'confirm-delete'
type SubmissionOption = {value: string; label: string}

const outcomeOptions: Array<{type: FoundPlaceOutcomeType; title: string; description: string}> = [
  {
    type: 'submitted_property',
    title: 'I secured a submitted property',
    description: 'Choose a property from your household submissions.',
  },
  {
    type: 'elsewhere',
    title: 'I found a property elsewhere',
    description: 'Withdraw your active household applications.',
  },
  {
    type: 'not_found',
    title: 'I have not found a property',
    description: 'Get rental support from a Helper.',
  },
]

const FoundPlaceModal: React.FC<FoundPlaceModalProps> = ({
  isOpen,
  submissions,
  initialView = 'options',
  onClose,
  onConfirm,
  onDelete,
  onRevokeAll,
}) => {
  const [view, setView] = useState<FoundPlaceModalView>(initialView)
  const [outcomeType, setOutcomeType] = useState<FoundPlaceOutcomeType>('submitted_property')
  const [selectedSubmissionId, setSelectedSubmissionId] = useState('')
  const [isConfirming, setIsConfirming] = useState(false)
  const confirmingRef = useRef(false)
  const [confirmationError, setConfirmationError] = useState<string | null>(null)
  const [showHelper, setShowHelper] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleteReturnView, setDeleteReturnView] = useState<FoundPlaceModalView | null>(null)
  const [isRevokingAll, setIsRevokingAll] = useState(false)
  const revokingAllRef = useRef(false)
  const [revokeAllError, setRevokeAllError] = useState<string | null>(null)

  const closeModal = (force = false) => {
    if (confirmingRef.current || revokingAllRef.current || isDeleting) return
    if (!force && (view === 'card-options' || view === 'confirm-delete')) return
    setView('options')
    setSelectedSubmissionId('')
    setDeleteError(null)
    setDeleteReturnView(null)
    onClose()
  }

  const handleDismiss = () => closeModal(false)
  const handleAnsweredClose = () => closeModal(true)

  const handleRevokeAll = async () => {
    if (revokingAllRef.current) return
    revokingAllRef.current = true
    setIsRevokingAll(true)
    setRevokeAllError(null)
    try {
      await onRevokeAll()
      revokingAllRef.current = false
      closeModal(true)
    } catch (error) {
      setRevokeAllError(
        error instanceof Error ? error.message : 'Unable to revoke submissions right now. Please try again.'
      )
    } finally {
      revokingAllRef.current = false
      setIsRevokingAll(false)
    }
  }

  const handleDelete = async () => {
    if (isDeleting) {
      return
    }

    setDeleteError(null)
    setIsDeleting(true)
    try {
      await onDelete()
    } catch (error) {
      setDeleteError(
        error instanceof Error ? error.message : 'Unable to delete your Doora Card right now.'
      )
    } finally {
      setIsDeleting(false)
    }
  }

  const openDeleteConfirmation = (returnView: FoundPlaceModalView) => {
    setDeleteError(null)
    setDeleteReturnView(returnView)
    setView('confirm-delete')
  }

  const handleCancelDelete = () => {
    if (deleteReturnView) {
      setDeleteError(null)
      setView(deleteReturnView)
      setDeleteReturnView(null)
      return
    }

    closeModal(true)
  }

  const submissionOptions: SubmissionOption[] = submissions
    .filter(isLiveOutcomeSubmission)
    .map((submission) => ({
      value: submission.id,
      label: submission.address,
    }))
  const selectedSubmission =
    submissionOptions.find((option) => option.value === selectedSubmissionId) ?? null
  const isSuccessfulOutcome = outcomeType !== 'not_found'
  const canConfirm =
    !isConfirming && (outcomeType !== 'submitted_property' || Boolean(selectedSubmission))

  const handleConfirm = async (
    nextOutcomeType = outcomeType,
    nextSubmissionId = nextOutcomeType === 'submitted_property' ? selectedSubmissionId : null
  ) => {
    const canConfirmNext =
      !isConfirming &&
      (nextOutcomeType !== 'submitted_property' ||
        submissionOptions.some((option) => option.value === nextSubmissionId))
    if (confirmingRef.current || !canConfirmNext) return

    const submissionId = nextOutcomeType === 'submitted_property' ? nextSubmissionId : null
    confirmingRef.current = true
    setOutcomeType(nextOutcomeType)
    setConfirmationError(null)
    setIsConfirming(true)
    try {
      await onConfirm({outcomeType: nextOutcomeType, submissionId})
      if (nextOutcomeType === 'not_found') {
        setView('helper')
      } else {
        setView('card-options')
      }
    } catch (error) {
      setConfirmationError(
        error instanceof Error ? error.message : 'Unable to confirm your choice right now.'
      )
    } finally {
      confirmingRef.current = false
      setIsConfirming(false)
    }
  }

  const handleOutcomeOptionClick = (nextOutcomeType: FoundPlaceOutcomeType) => {
    setOutcomeType(nextOutcomeType)
    setConfirmationError(null)

    if (nextOutcomeType === 'not_found') {
      void handleConfirm('not_found', null)
      return
    }

    setView('details')
  }

  if (!isOpen) return null
  if (showHelper) return <MessageHelperModal isOpen={true} onClose={() => setShowHelper(false)} />

  const canContinueDelete = !isDeleting
  const mustAnswerCardChoice = view === 'card-options' || view === 'confirm-delete'

  return (
    <ApplicationResponsiveModal
      ariaLabelledBy='found-place-modal-title'
      closeAriaLabel='Close found a place dialog'
      onClose={handleDismiss}
      modalClassName='application-household-modal'
      closeButtonClassName='application-household-modal-close'
      disableDismiss={mustAnswerCardChoice || isRevokingAll || isDeleting}
      mobileAnimationMs={500}
      swipeTarget='sheet'
    >
      <div className='application-household-modal-body foundaplace'>
        {view === 'delete-options' ? (
          <>
            <div className='head'>
              <h2 id='found-place-modal-title'>Delete Everything</h2>
              <p>Choose what happens to your DOORA Card and property submissions.</p>
            </div>
            <div className='body' aria-busy={isRevokingAll}>
              <button type='button' disabled={isRevokingAll} onClick={handleRevokeAll}>
                <div className='info'>
                  <div className='title'>
                    {isRevokingAll ? 'Revoking submissions...' : 'Keep DOORA Card, Revoke All Submissions'}
                  </div>
                  <div className='description'>
                    Revoke all active household property submissions and remove property manager access.
                    Your card, applicant information, and household relationships stay unchanged.
                  </div>
                </div>
              </button>
              <button
                type='button'
                className='delete-card-option'
                disabled={isRevokingAll}
                onClick={() => openDeleteConfirmation('delete-options')}
              >
                <div className='info'>
                  <div className='title'>Delete My DOORA Card</div>
                  <div className='description'>
                    Continue to confirmation to permanently delete your own card.
                  </div>
                </div>
              </button>
            </div>
            {revokeAllError && <div className='delete-help' role='alert'>{revokeAllError}</div>}
            <button type='button' className='back' disabled={isRevokingAll} onClick={handleAnsweredClose}>
              Cancel
            </button>
          </>
        ) : view === 'details' ? (
          <form
            onSubmit={(event) => {
              event.preventDefault()
              void handleConfirm()
            }}
          >
            <div className='head'>
              <h2 id='found-place-modal-title'>
                {isSuccessfulOutcome ? 'Congratulations!' : 'Your property search'}
              </h2>
              <p>
                {isSuccessfulOutcome
                  ? 'Confirm your choice, then choose what happens to your card.'
                  : 'Continue to message a Helper.'}
              </p>
            </div>
            {outcomeType === 'submitted_property' && (
              <div className='secure-property'>
                <div className='inner'>
                  <label htmlFor='found-place-submission'>Which property did you secure?</label>
                  <span className='pill pill-req'>Required</span>
                </div>
                <Select<SubmissionOption, false>
                  inputId='found-place-submission'
                  className='found-place-select'
                  classNamePrefix='submission-select'
                  value={selectedSubmission}
                  options={submissionOptions}
                  placeholder='Select from your live submissions'
                  noOptionsMessage={() => 'No live submissions available.'}
                  isSearchable={false}
                  isDisabled={isConfirming}
                  onChange={(option) => setSelectedSubmissionId(option?.value ?? '')}
                />
              </div>
            )}
            {isSuccessfulOutcome && (
              <p className='delete-help'>
                Confirming will withdraw all active applications for your household, including those
                sent by co-applicants. Property managers will no longer be able to access them. You
                can keep your Doora Card active.
              </p>
            )}
            {confirmationError && (
              <div className='delete-help' role='alert'>
                {confirmationError}
              </div>
            )}
            <button type='submit' className='confirm mt-5' disabled={!canConfirm}>
              {isConfirming
                ? 'Confirming...'
                : isSuccessfulOutcome
                ? 'Confirm and withdraw applications'
                : 'Continue'}
            </button>
            <button
              type='button'
              className='back'
              disabled={isConfirming}
              onClick={() => setView('options')}
            >
              Back
            </button>
          </form>
        ) : view === 'card-options' ? (
          <>
            <div className='head'>
              <h2 id='found-place-modal-title'>Applications withdrawn</h2>
              <p>
                All active household applications have been withdrawn. Choose what happens to your
                Doora Card now.
              </p>
            </div>
            <div className='body'>
              <button type='button' onClick={handleAnsweredClose}>
                <div className='info'>
                  <div className='title'>Keep my Doora Card</div>
                  <div className='description'>
                    Your card stays active and you can continue using it.
                  </div>
                </div>
              </button>
              <button
                type='button'
                className='delete-card-option'
                onClick={() => {
                  openDeleteConfirmation('card-options')
                }}
              >
                <div className='info'>
                  <div className='title'>Delete my Doora Card</div>
                  <div className='description'>Permanently remove your profile and documents.</div>
                </div>
              </button>
            </div>
          </>
        ) : view === 'helper' ? (
          <>
            <div className='head'>
              <h2 id='found-place-modal-title'>Before you go</h2>
              <p>A Helper can support you with your property search.</p>
            </div>
            <div className='delete-help'>
              <p>
                You have not secured a property. Message a Helper for rental support, or continue to
                the deletion confirmation.
              </p>
              <div className='delete-help-actions'>
                <button
                  type='button'
                  className='talk-to-someone'
                  onClick={() => setShowHelper(true)}
                >
                  Message a Helper
                </button>
                <button
                  type='button'
                  className='no-thanks'
                  onClick={() => openDeleteConfirmation('helper')}
                >
                  Continue to deletion
                </button>
              </div>
            </div>
            <button type='button' className='back' onClick={handleAnsweredClose}>
              Keep my Doora Card
            </button>
          </>
        ) : view === 'confirm-delete' ? (
          <>
            <div className='head'>
              <div className='icon'>
                <svg
                  width='26'
                  height='26'
                  viewBox='0 0 20 20'
                  fill='none'
                  stroke='#EF4444'
                  strokeWidth='1.6'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M4 5.5h12M8 5.5V4a1.5 1.5 0 011.5-1.5h1A1.5 1.5 0 0112 4v1.5M5.5 5.5l.8 10A1.5 1.5 0 007.8 17h4.4a1.5 1.5 0 001.5-1.5l.8-10'></path>
                </svg>
              </div>
              <h2 id='found-place-modal-title'>Delete your Doora Card?</h2>
              <p>This permanently removes your profile and cannot be undone.</p>
            </div>
            <div className='declined-help'>
              <div className='item'>All related property submissions will be revoked.</div>
              <div className='item'>
                Your profile, documents, and personal information will be permanently deleted.
              </div>
              <div className='item'>You cannot undo this action.</div>
            </div>
            {deleteError && <div className='delete-help'>{deleteError}</div>}
            <button
              type='button'
              className='continue-delete mt-5'
              disabled={!canContinueDelete}
              onClick={handleDelete}
            >
              <svg
                width='16'
                height='16'
                viewBox='0 0 20 20'
                fill='none'
                stroke='#fff'
                strokeWidth='2.4'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M4 5.5h12M8 5.5V4a1.5 1.5 0 011.5-1.5h1A1.5 1.5 0 0112 4v1.5M5.5 5.5l.8 10A1.5 1.5 0 007.8 17h4.4a1.5 1.5 0 001.5-1.5l.8-10'></path>
              </svg>{' '}
              {isDeleting ? 'Deleting...' : 'Delete my Doora Card'}
            </button>
            <button
              type='button'
              className='back'
              disabled={isDeleting}
              onClick={handleCancelDelete}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <div className='head'>
              <h2 id='found-place-modal-title'>Found a place</h2>
              <p>Tell us the outcome of your property search.</p>
            </div>
            <div className='body'>
              {outcomeOptions.map((option) => (
                <button
                  type='button'
                  key={option.type}
                  disabled={isConfirming}
                  onClick={() => {
                    handleOutcomeOptionClick(option.type)
                  }}
                >
                  <div className='info'>
                    <div className='title'>{option.title}</div>
                    <div className='description'>{option.description}</div>
                  </div>
                </button>
              ))}
            </div>
            {confirmationError && (
              <div className='delete-help' role='alert'>
                {confirmationError}
              </div>
            )}
            <button type='button' className='cancel' onClick={handleDismiss}>
              Cancel
            </button>
          </>
        )}
      </div>
    </ApplicationResponsiveModal>
  )
}

export default FoundPlaceModal
